/**
 * Import Manager Component
 * UI for managing ADO data imports
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Assessment as AssessmentIcon,
  Build as BuildIcon,
  CloudDownload as ImportIcon,
  HealthAndSafety as HealthIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import axios from 'axios';

const ImportManager = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [recentImports, setRecentImports] = useState([]);
  const [runningImport, setRunningImport] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialog states
  const [dailyImportDialog, setDailyImportDialog] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  
  // Form states
  const [dailyImportConfig, setDailyImportConfig] = useState({
    lookbackHours: 30,
    dryRun: false
  });
  
  const [scheduleConfig, setScheduleConfig] = useState({
    enabled: false,
    schedule: '0 2 * * *',
    lookbackHours: 30,
    retryOnFailure: true
  });

  // Fetch import status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/import/status');
      setStatus(response.data);
      setRecentImports(response.data.recentImports || []);
      setRunningImport(response.data.runningImport);
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  }, []);

  // Fetch health status
  const fetchHealth = useCallback(async () => {
    try {
      const response = await axios.get('/api/import/health');
      setHealth(response.data.health);
    } catch (err) {
      console.error('Error fetching health:', err);
    }
  }, []);

  // Fetch schedule configuration
  const fetchSchedule = useCallback(async () => {
    try {
      const response = await axios.get('/api/import/schedule');
      setSchedule(response.data);
      setScheduleConfig(response.data.config?.dailyImport || scheduleConfig);
    } catch (err) {
      console.error('Error fetching schedule:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchStatus();
    fetchHealth();
    fetchSchedule();
  }, [fetchStatus, fetchHealth, fetchSchedule]);

  // Import handlers
  const handleDailyImport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post('/api/import/daily', dailyImportConfig);
      setSuccess(response.data.message);
      setDailyImportDialog(false);
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start daily import');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSchedule = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.put('/api/import/schedule', scheduleConfig);
      setSuccess(response.data.message);
      setScheduleDialog(false);
      fetchSchedule();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelImport = async (importId) => {
    if (!window.confirm('Are you sure you want to cancel this import?')) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`/api/import/cancel/${importId}`);
      setSuccess(response.data.message);
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel import');
    } finally {
      setLoading(false);
    }
  };

  // Status helpers
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'in-progress':
        return <CircularProgress size={20} />;
      case 'cancelled':
        return <StopIcon color="action" />;
      default:
        return <InfoIcon color="action" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'in-progress':
        return 'info';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return '-';
    const duration = new Date(end) - new Date(start);
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Import Manager
        </Typography>
        <Box>
          <IconButton onClick={fetchStatus} disabled={loading} title="Refresh status">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Running Import Status */}
      {runningImport && (
        <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="h6">
                Import in Progress
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<StopIcon />}
                  onClick={() => handleCancelImport(runningImport._id)}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">Type</Typography>
                <Typography variant="body1">{runningImport.type}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">Started</Typography>
                <Typography variant="body1">
                  {formatDistanceToNow(new Date(runningImport.startTime), { addSuffix: true })}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Configuration</Typography>
                <Typography variant="body1">
                  {JSON.stringify(runningImport.config)}
                </Typography>
              </Grid>
            </Grid>
            <LinearProgress sx={{ mt: 2 }} />
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ImportIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Daily Import</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Import changes from the last 30 hours
              </Typography>
              <Button
                variant="contained"
                fullWidth
                startIcon={<PlayIcon />}
                onClick={() => setDailyImportDialog(true)}
                disabled={loading || !!runningImport}
              >
                Run Daily Import
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Schedule</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {schedule?.status?.enabled ? 
                  `Next run: ${schedule.status.nextRun || 'Not scheduled'}` : 
                  'Automatic imports disabled'}
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<SettingsIcon />}
                onClick={() => setScheduleDialog(true)}
              >
                Configure Schedule
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HealthIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Health Status</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {health?.status === 'healthy' ? 
                  'All systems operational' : 
                  health?.status || 'Checking...'}
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<HealthIcon />}
                onClick={fetchHealth}
              >
                Check Health
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
          <Tab label="Recent Imports" icon={<HistoryIcon />} iconPosition="start" />
          <Tab label="Schedule" icon={<ScheduleIcon />} iconPosition="start" />
          <Tab label="Health" icon={<HealthIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Configuration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentImports.map((imp) => (
                <TableRow key={imp._id}>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(imp.status)}
                      label={imp.status}
                      color={getStatusColor(imp.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{imp.type}</TableCell>
                  <TableCell>
                    {format(new Date(imp.startTime), 'MMM dd, HH:mm')}
                  </TableCell>
                  <TableCell>
                    {formatDuration(imp.startTime, imp.endTime)}
                  </TableCell>
                  <TableCell>
                    {imp.itemsProcessed && (
                      <Box>
                        <Typography variant="caption" display="block">
                          Processed: {imp.itemsProcessed}
                        </Typography>
                        {imp.itemsFailed > 0 && (
                          <Typography variant="caption" color="error">
                            Failed: {imp.itemsFailed}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {JSON.stringify(imp.config)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Schedule Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Status
                </Typography>
                <Typography variant="body1">
                  {schedule?.status?.enabled ? 'Enabled' : 'Disabled'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Schedule
                </Typography>
                <Typography variant="body1">
                  {schedule?.config?.dailyImport?.schedule || 'Not configured'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {selectedTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              System Health
            </Typography>
            {health && (
              <List>
                <ListItem>
                  <ListItemIcon>
                    {health.database?.connected ? 
                      <CheckIcon color="success" /> : 
                      <ErrorIcon color="error" />}
                  </ListItemIcon>
                  <ListItemText
                    primary="Database"
                    secondary={health.database?.connected ? 'Connected' : 'Disconnected'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {health.ado?.connected ? 
                      <CheckIcon color="success" /> : 
                      <ErrorIcon color="error" />}
                  </ListItemIcon>
                  <ListItemText
                    primary="Azure DevOps"
                    secondary={health.ado?.connected ? 'Connected' : 'Connection failed'}
                  />
                </ListItem>
              </List>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      
      {/* Daily Import Dialog */}
      <Dialog open={dailyImportDialog} onClose={() => setDailyImportDialog(false)}>
        <DialogTitle>Run Daily Import</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Lookback Hours"
              type="number"
              value={dailyImportConfig.lookbackHours}
              onChange={(e) => setDailyImportConfig({
                ...dailyImportConfig,
                lookbackHours: parseInt(e.target.value)
              })}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={dailyImportConfig.dryRun}
                  onChange={(e) => setDailyImportConfig({
                    ...dailyImportConfig,
                    dryRun: e.target.checked
                  })}
                />
              }
              label="Dry Run (Preview Only)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDailyImportDialog(false)}>Cancel</Button>
          <Button onClick={handleDailyImport} variant="contained" disabled={loading}>
            Run Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Configuration Dialog */}
      <Dialog open={scheduleDialog} onClose={() => setScheduleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configure Schedule</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={scheduleConfig.enabled}
                  onChange={(e) => setScheduleConfig({
                    ...scheduleConfig,
                    enabled: e.target.checked
                  })}
                />
              }
              label="Enable Automatic Imports"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Cron Schedule"
              value={scheduleConfig.schedule}
              onChange={(e) => setScheduleConfig({
                ...scheduleConfig,
                schedule: e.target.value
              })}
              helperText="e.g., '0 2 * * *' for daily at 2 AM"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Lookback Hours"
              type="number"
              value={scheduleConfig.lookbackHours}
              onChange={(e) => setScheduleConfig({
                ...scheduleConfig,
                lookbackHours: parseInt(e.target.value)
              })}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={scheduleConfig.retryOnFailure}
                  onChange={(e) => setScheduleConfig({
                    ...scheduleConfig,
                    retryOnFailure: e.target.checked
                  })}
                />
              }
              label="Retry on Failure"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateSchedule} variant="contained" disabled={loading}>
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImportManager;
